import type { ReactElement, ReactNode } from "react";

import { BottomNav } from "@/components/layout/BottomNav";
import {
  GlobalSearchIconButton,
  GlobalSearchProvider,
  GlobalSearchTrigger,
} from "@/components/layout/GlobalSearch";
import { PageScrollProvider, PageScrollViewport } from "@/components/layout/PageScrollContext";
import { BrandLogoLink, Sidebar } from "@/components/layout/Sidebar";
import { OrganizationSidebarSwitcher } from "@/components/layout/OrganizationSidebarSwitcher";
import { ProductTourProvider } from "@/components/product-tour/ProductTourProvider";
import { PageSuspense } from "@/components/shared/PageSuspense";
import { PermissionGate } from "@/components/shared/PermissionGate";
import { HelpAiShortcutButton } from "@/components/layout/HelpAiShortcutButton";
import { HelpChatDock } from "@/components/layout/HelpChatDock";
import { HelpChatDrawer } from "@/components/layout/HelpChatDrawer";
import { HelpProvider } from "@/components/layout/HelpProvider";
import { HelpShortcutButton } from "@/components/layout/HelpShortcutButton";
import { SettingsShortcutButton } from "@/components/layout/SettingsShortcutButton";
import { NotificationsBell } from "@/components/layout/NotificationsBell";
import { UserAccountMenu } from "@/components/layout/UserAccountMenu";
import { BillingGraceBanner } from "@/components/BillingGraceBanner";
import {
  APP_CHROME_PRIMARY_ROW_CLASS,
  SIDEBAR_WIDTH_COLLAPSED_CLASS,
  SIDEBAR_WIDTH_EXPANDED_CLASS,
} from "@/components/layout/appChrome";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useRememberSettingsReturnTo } from "@/hooks/useRememberSettingsReturnTo";
import { hasNavigableAppAccess } from "@/lib/permissions";
import { useSidebarNavState } from "@/navigation/sidebarRegistry";
import type { SidebarId } from "@/navigation/types";
import { useUiStore } from "@/store/uiStore";

function shouldShowDesktopSidebar(sidebarId: SidebarId, showAppNav: boolean): boolean {
  switch (sidebarId) {
    case "app":
    case "project":
      return showAppNav;
    case "settings":
    case "account":
      return true;
    default: {
      const _exhaustive: never = sidebarId;
      return _exhaustive;
    }
  }
}

const toolbarBaseCls = cn(APP_CHROME_PRIMARY_ROW_CLASS, "flex items-center");

type AppShellProps = {
  /** When set, renders this instead of the routed {@link PermissionGate} outlet (e.g. standalone 404). */
  children?: ReactNode;
};

export function AppShell({ children }: AppShellProps): ReactElement {
  const { user } = useAuth();
  const { sidebarId } = useSidebarNavState();
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  useRememberSettingsReturnTo();
  const showAppNav = hasNavigableAppAccess(user);
  const showDesktopSidebar = shouldShowDesktopSidebar(sidebarId, showAppNav);
  const showBottomNav = showAppNav || sidebarId === "account" || sidebarId === "settings";
  const showSearch = showAppNav;

  return (
    <GlobalSearchProvider>
      <ProductTourProvider>
        <HelpProvider>
          <div className="flex h-dvh max-h-dvh min-h-0 flex-col overflow-hidden text-foreground">
            <BillingGraceBanner />
            <header
              className={cn(toolbarBaseCls, "flex min-w-0 items-center gap-2 px-3 lg:hidden")}
            >
              {user?.organization ? (
                <div className="min-w-0">
                  <OrganizationSidebarSwitcher />
                </div>
              ) : (
                <BrandLogoLink
                  className="min-w-0 flex-1 gap-2"
                  logoClassName="size-7 shrink-0"
                  headingVariant="chrome"
                />
              )}
              <div className="ml-auto flex shrink-0 items-center gap-0">
                {showSearch ? <GlobalSearchIconButton /> : null}
                <HelpAiShortcutButton />
                <NotificationsBell />
                <UserAccountMenu variant="header" includeChromeShortcuts />
              </div>
            </header>

            <header className={cn(toolbarBaseCls, "hidden w-full items-center lg:flex")}>
              <div
                className={cn(
                  "flex h-full min-w-0 shrink-0 items-center px-4 transition-[width] duration-200 ease-out",
                  showDesktopSidebar
                    ? sidebarCollapsed
                      ? SIDEBAR_WIDTH_COLLAPSED_CLASS
                      : SIDEBAR_WIDTH_EXPANDED_CLASS
                    : "gap-6",
                )}
              >
                {user?.organization ? (
                  <OrganizationSidebarSwitcher compact={sidebarCollapsed} />
                ) : (
                  <BrandLogoLink showLabel={!sidebarCollapsed} />
                )}
              </div>
              <div
                className={cn(
                  "flex min-w-0 flex-1 items-center",
                  showDesktopSidebar ? "pr-4" : "justify-center px-4",
                )}
              >
                {showSearch ? <GlobalSearchTrigger className="w-full max-w-[44rem]" /> : null}
              </div>
              <div className="flex shrink-0 items-center gap-1 px-4">
                <HelpAiShortcutButton />
                <HelpShortcutButton />
                <SettingsShortcutButton />
                <NotificationsBell />
                <UserAccountMenu variant="topbar" />
              </div>
            </header>

            <div
              className={cn(
                "flex min-h-0 min-w-0 flex-1 flex-col bg-background lg:flex-row lg:items-stretch",
                showDesktopSidebar && "lg:bg-sidebar",
              )}
            >
              {showDesktopSidebar ? <Sidebar /> : null}

              <main
                className={cn(
                  "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background lg:flex-row",
                  showDesktopSidebar && "lg:rounded-tl-2xl",
                )}
              >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <PageScrollProvider>
                    <PageScrollViewport>
                      <PageSuspense>{children ?? <PermissionGate />}</PageSuspense>
                    </PageScrollViewport>
                  </PageScrollProvider>
                </div>
                <HelpChatDock />
              </main>
            </div>

            {showBottomNav ? <BottomNav /> : null}
            <HelpChatDrawer />
          </div>
        </HelpProvider>
      </ProductTourProvider>
    </GlobalSearchProvider>
  );
}
